import KatalLogger, { Level } from '@amzn/katal-logger';
import { fetchAuthSession } from 'aws-amplify/auth';

import { ProvisionedConstants, LOGGING_TIMEOUT_MS } from './constants';
const isDev: boolean = ProvisionedConstants.STAGE === 'Dev';

class AuthenticatedLogger {
  private logger: KatalLogger | null = null;
  private currentToken: string | null = null;
  private initPromise: Promise<KatalLogger> | null = null;

  private getLogger = async (): Promise<KatalLogger> => {
    console.debug('[Logger] getLogger called');
    if (this.initPromise) {
      console.debug('[Logger] Returning existing init promise');
      return this.initPromise;
    }

    console.debug('[Logger] Starting logger initialization');
    this.initPromise = this.initializeLogger();
    try {
      const logger = await this.initPromise;
      this.initPromise = null;
      console.debug('[Logger] Logger initialization completed successfully');
      return logger;
    } catch (error) {
      this.initPromise = null;
      console.debug('[Logger] Logger initialization failed');
      throw error;
    }
  };

  private initializeLogger = async (): Promise<KatalLogger> => {
    console.debug('[Logger] initializeLogger called');
    try {
      console.debug('[Logger] Fetching auth session');
      const session = await fetchAuthSession();
      console.debug('[Logger] Auth session fetched');
      const newToken = session.tokens?.idToken?.toString() || null;

      if (this.currentToken !== newToken || !this.logger) {
        console.debug('[Logger] Creating new logger instance');
        this.currentToken = newToken;
        this.logger = new KatalLogger({
          url: ProvisionedConstants.KATAL_LOGGER_URL,
          logThreshold: isDev ? Level.DEBUG : Level.INFO,
          maxLogLineSize: 10000,
          maxLogsPerBatch: 10,
          logToConsole: isDev,
          recordMetrics: false,
          sendToBackendOverride: this.sendToBackend,
        });
        this.logger.addErrorListener();
        console.debug('[Logger] New KatalLogger instance created');
      } else {
        console.debug('[Logger] Using existing logger instance');
      }
      return this.logger;
    } catch (error) {
      console.warn('Failed to refresh auth token for logging');
      console.debug(
        '[Logger] Auth session refresh failed, error message:',
        (error as Error)?.message || 'Unknown error',
      );
      if (this.logger) {
        console.debug('[Logger] Returning existing logger instance as fallback');
        return this.logger;
      }
      console.debug('[Logger] No existing logger available, throwing error');
      throw error;
    }
  };

  private sendToBackend = async (logData: string): Promise<void> => {
    console.debug('[Logger] sendToBackend called with data length:', logData.length);
    console.debug('[Logger] Using timeout:', LOGGING_TIMEOUT_MS, 'ms');
    let startTime = Date.now();
    try {
      if (!this.currentToken) {
        console.warn('No ID token available for logging');
        console.debug('[Logger] sendToBackend aborted - authentication required');
        return;
      }

      console.debug('[Logger] Sending logs to backend:', ProvisionedConstants.KATAL_LOGGER_URL);
      console.debug('[Logger] Token available:', !!this.currentToken);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.debug('[Logger] Request timed out after', LOGGING_TIMEOUT_MS, 'ms');
        controller.abort();
      }, LOGGING_TIMEOUT_MS);

      startTime = Date.now();
      try {
        const response = await fetch(ProvisionedConstants.KATAL_LOGGER_URL, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${this.currentToken}`,
            'Content-Type': 'application/json',
          },
          body: logData,
          signal: controller.signal,
        });

        console.debug('[Logger] Request completed in', Date.now() - startTime, 'ms');
        console.debug('[Logger] Backend response status:', response.status);
        if (!response.ok) {
          throw new Error(`Failed to send logs: ${response.status} ${response.statusText}`);
        }
        console.debug('[Logger] Logs sent successfully to backend');
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (error) {
      console.debug('[Logger] Request failed after', Date.now() - startTime, 'ms');
      if ((error as Error)?.name === 'AbortError') {
        console.debug('[Logger] Request was aborted (likely timeout)');
      }
      console.warn('Failed to send logs:', error);
      console.debug(
        '[Logger] sendToBackend failed with error message:',
        (error as Error)?.message || 'Unknown error',
      );
    }
  };

  public info = async (message: string, context?: Record<string, unknown>): Promise<void> => {
    console.debug('[Logger] info called with message:', message, 'context:', context);
    (await this.getLogger()).info(message, context);
  };

  public error = async (message: string, context?: Record<string, unknown>): Promise<void> => {
    console.debug('[Logger] error called with message:', message, 'context:', context);
    (await this.getLogger()).error(message, context);
  };

  public debug = async (message: string, context?: Record<string, unknown>): Promise<void> => {
    console.debug('[Logger] debug called with message:', message, 'context:', context);
    (await this.getLogger()).debug(message, context);
  };

  public warn = async (message: string, context?: Record<string, unknown>): Promise<void> => {
    console.debug('[Logger] warn called with message:', message, 'context:', context);
    (await this.getLogger()).warn(message, context);
  };
}

export const logger = new AuthenticatedLogger();
console.debug('[Logger] AuthenticatedLogger instance created');
